import React, { useState, useEffect } from 'react';
import './Roles.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [currentOrgId, setCurrentOrgId] = useState('');
  const [currentOrgName, setCurrentOrgName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    loadOrgAndRoles();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      fetchPermissions();
    }
  }, [isModalOpen]);

  const loadOrgAndRoles = async () => {
    try {
      setLoading(true);
      const societyId = localStorage.getItem('societyId');
      const societiesData = localStorage.getItem('societies');
      
      if (!societyId || !societiesData) {
        setError('No organization selected');
        return;
      }

      const societies = JSON.parse(societiesData);
      const currentSociety = societies.find(org => org.OrgId === societyId);
      
      if (!currentSociety) {
        setError('Organization not found');
        return;
      }

      setCurrentOrgId(currentSociety.OrgId);
      setCurrentOrgName(currentSociety.Name);

      // Fetch roles for the organization
      await fetchRoles(currentSociety.OrgId);
    } catch (error) {
      console.error('Error loading organization:', error);
      setError('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async (orgId) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${orgId}/roles?access_token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Roles API response:', data);
        
        // Try different possible data paths
        const rolesData = data.data?.Data || data.data || data.Data || data || [];
        console.log('Extracted roles:', rolesData);
        setRoles(rolesData);
      } else {
        throw new Error('Failed to fetch roles');
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError('Failed to load roles');
    }
  };

  const fetchPermissions = async () => {
    try {
      setLoadingPermissions(true);
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      const response = await fetch(
        `${API_URL}/api/v1/auth/permissions?access_token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAllPermissions(data.data?.Data || []);
      } else {
        throw new Error('Failed to fetch permissions');
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Failed to load permissions');
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => {
      const permissions = prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId];
      return {
        ...prev,
        permissions
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentOrgId) {
      setError('No organization selected');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess('');

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('No access token found. Please log in again.');
        return;
      }

      const payload = {
        Name: formData.name,
        Description: formData.description || undefined
      };

      // Add permissions to payload if any are selected
      if (formData.permissions.length > 0) {
        payload.Permissions = formData.permissions;
      }

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${currentOrgId}/roles?access_token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
      setSuccess('Role created successfully!');
      setFormData({
        name: '',
        description: '',
        permissions: []
      });
        setIsModalOpen(false);
        
        // Refresh roles list
        await fetchRoles(currentOrgId);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create role');
      }
    } catch (error) {
      console.error('Error creating role:', error);
      setError(error.message || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setError(null);
    setFormData({
      name: '',
      description: '',
      permissions: []
    });
  };

  if (loading) {
    return <div className="roles-loading">Loading roles...</div>;
  }

  if (error && !currentOrgId) {
    return <div className="roles-error">{error}</div>;
  }

  return (
    <div className="roles-container">
      <div className="roles-header">
        <div>
          <h2>Organization Roles</h2>
          <p className="org-subtitle">{currentOrgName}</p>
        </div>
        <button
          className="btn-add-role"
          onClick={() => setIsModalOpen(true)}
        >
          <i className="fas fa-plus"></i>
          Add Role
        </button>
      </div>

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="roles-list">
        {roles.length === 0 ? (
          <div className="no-roles">
            <i className="fas fa-user-tag"></i>
            <p>No roles found. Click "Add Role" to create one.</p>
          </div>
        ) : (
          roles.map((role) => (
            <div key={role.Id} className="role-card">
              <div className="role-card-header">
                <h3>{role.Name}</h3>
              </div>
              <div className="role-card-body">
                <p className="role-description">{role.Description || 'No description provided'}</p>
                {role.Permissions && role.Permissions.length > 0 && (
                  <div className="role-permissions">
                    <strong>Permissions:</strong>
                    <div className="permissions-list">
                      {role.Permissions.map((permission, index) => (
                        <span key={index} className="permission-badge">
                          {permission.Name || permission}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Role</h3>
              <button 
                className="close-button"
                onClick={closeModal}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleSubmit} className="role-form">
              <div className="form-group">
                <label htmlFor="name">Role Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter role name (e.g., Manager)"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description (Optional)</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter role description"
                  className="form-control"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Permissions (Optional)</label>
                {loadingPermissions ? (
                  <div className="select-loading">Loading permissions...</div>
                ) : (
                  <div className="permissions-selector">
                    {allPermissions.length === 0 ? (
                      <p className="no-permissions-message">No permissions available</p>
                    ) : (
                      <div className="permissions-checkboxes">
                        {allPermissions.map((permission) => (
                          <label key={permission.Id} className="permission-checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission.Id)}
                              onChange={() => handlePermissionToggle(permission.Id)}
                              disabled={saving}
                            />
                            <div className="permission-info">
                              <span className="permission-name">{permission.Name}</span>
                              <span className="permission-desc">{permission.Description}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;

